<template>
  <div id="pos-return-print-area"
       class="pos-receipt"
       :style="{ width: printWidth, maxWidth: printWidth }"
  >

    <component :is="'style'">
      @media print {
      @page { size: {{ printWidth }} auto; }
      #pos-return-print-area-portal,
      #pos-return-print-area { width: {{ printWidth }} !important; }
      }
    </component>

    <!-- Company Header -->
    <div class="text-center company-info">
      <img v-if="company.logo64 || company.logo" :src="company.logo64 || company.logo" class="company-logo" alt="logo" />
      <h4 v-if="company.name" class="company-name">{{ company.name }}</h4>
      <h6 v-if="company.address" class="company-address">{{ company.address }}</h6>
      <h6 v-if="company.phone" class="company-address">Mobile: {{ company.phone }}</h6>
      <h6 v-if="company.vat_no" class="company-address">VAT Reg No: {{ company.vat_no }}</h6>
    </div>

    <div class="divider-dashed"></div>

    <div class="text-center return-title">SALES RETURN</div>

    <!-- Return & Reference Info -->
    <div class="info-grid">
      <div class="info-row">
        <span class="info-label">Date: </span>
        <span class="info-value">{{ formatDate(saleReturn.date) }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Return#: </span>
        <span class="info-value">{{ saleReturn.bill_number }}</span>
      </div>
      <div class="info-row" v-if="originalBillNumber">
        <span class="info-label">Against Inv#: </span>
        <span class="info-value">{{ originalBillNumber }}</span>
      </div>
    </div>

    <div class="divider-dashed"></div>

    <!-- Items Table -->
    <table class="pos-table">
      <thead>
      <tr>
        <th class="col-sl">#</th>
        <th class="col-item">Item</th>
        <th class="col-num">Qty</th>
        <th class="col-num">Price</th>
        <th class="col-amt">Amt</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(item, i) in items" :key="i">
        <td class="col-sl">{{ i + 1 }}</td>
        <td class="col-item">
          {{ saleReturn.has_item_detail ? item.product.name : item.head.name }}
        </td>
        <td class="col-num">{{ item.quantity }}</td>
        <td class="col-num">{{ formatNumber(item.rate) }}</td>
        <td class="col-amt">{{ formatNumber(item.quantity * item.rate) }}</td>
      </tr>
      <tr v-if="items.length === 0">
        <td colspan="5" class="text-center" style="padding: 4px 0; font-style: italic;">No items</td>
      </tr>
      </tbody>
    </table>

    <div class="divider-dashed"></div>

    <!-- Summary -->
    <div class="summary-section">
      <div v-if="totalDiscount > 0" class="summary-row">
        <span>Total Return</span>
        <span>{{ formatNumber(saleReturn.subtotal_amount) }}</span>
      </div>
      <div v-if="totalDiscount > 0" class="summary-row">
        <span>Discount(-)</span>
        <span>{{ formatNumber(totalDiscount) }}</span>
      </div>
      <div class="summary-row summary-netbill">
        <span>Net Return</span>
        <span>{{ formatNumber(saleReturn.total_amount) }}</span>
      </div>
      <div class="summary-row" v-if="saleReturn.paid_amount > 0">
        <span>Refunded</span>
        <span>{{ formatNumber(saleReturn.paid_amount) }}</span>
      </div>
    </div>

    <!-- Footer -->
    <div class="text-center thank-you pt-2">{{ footerText }}</div>

  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  company: {
    type: Object,
    default: () => ({})
  },
  saleReturn: {
    type: Object,
    default: () => ({})
  },
  customizationPdfFields: {
    type: Object,
    default: () => ({})
  },
  posPrintSize: {
    type: Number,
    default: ''
  }
})

const printWidth = computed(() =>
    props.posPrintSize ? `${props.posPrintSize}mm` : '58mm'
)

const items = computed(() => {
  if (empty(props.saleReturn)) return []
  return props.saleReturn.has_item_detail
      ? props.saleReturn.general ?? []
      : props.saleReturn.ledgers ?? []
})

const totalDiscount = computed(() => Number(props.saleReturn.total_discount) || 0)

const originalBillNumber = computed(() => {
  if (empty(props.saleReturn.sale_master)) return ''
  return props.saleReturn.sale_master.bill_number
})

const footerText = computed(() => {
  if (empty(props.customizationPdfFields['extra_settings_footer_note'])) return null
  if (! props.customizationPdfFields['extra_settings_footer_note'].is_active) return null
  return props.customizationPdfFields['extra_settings_footer_note'].value
})

const formatNumber = (v) => Math.round(Number(v || 0))

const formatDate = (date) => {
  if (empty(date)) return ''
  const parts = date.split('-')
  if (parts.length !== 3) return date
  return `${parts[2]}-${parts[1]}-${parts[0]}`
}

const empty = (value) => {
  if (value === null || value === undefined || value === '' || value === false || value === 0) return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}
</script>

<style>
@media print {
  @page {
    margin: 0;
  }

  html, body {
    background: #fff !important;
    margin: 0 !important;
    padding: 0 !important;
    height: auto !important;
    min-height: 0 !important;
  }

  body > *:not(#pos-return-print-area-portal):not(#pos-print-area-portal) {
    display: none !important;
  }

  #pos-return-print-area-portal {
    display: block !important;
    visibility: visible !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }

  #pos-return-print-area {
    display: block !important;
    padding: 0 1mm!important;
    margin: 0 !important;
    background: #fff !important;
    font-family: 'Courier New', Courier, monospace !important;
    font-size: 11px !important;
    color: #000 !important;
    box-sizing: border-box !important;
  }
  .pos-table {
    display: table !important;
    width: 100% !important;
  }
}
</style>

<style scoped>
.pos-receipt {
  display: block;
}
.company-info {
  text-align: center;
  margin-top: 0 !important;
  margin-bottom: 2px;
  color:#000000;
}
.company-logo {
  height: 80px;
  width: 80px;
  object-fit: cover;
  display: block;
  margin: 0 auto 3px;
}
.company-name {
  font-size: 13px;
  font-weight: bold;
  margin: 2px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #000000;
}
.company-address {
  font-size: 10px;
  margin: 0.5px 0;
  line-height: 12px;
  color: #000000 !important;
  font-weight: bold;
}
.return-title {
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  color: #000000;
  margin: 2px 0;
}
.divider-dashed {
  border: none;
  border-top: 1px dashed #000;
  margin: 4px 0;
  width: 100%;
  font-weight: bold;
}
.info-grid {
  margin: 3px 0;
}
.info-row {
  display: flex;
  gap: 3px;
  margin-bottom: 2px;
  flex-wrap: wrap;
  font-size: 10px;
}
.info-label {
  font-weight: bold;
  font-size: 12px;
  white-space: nowrap;
  color: #000000;
}
.info-value{
  color:#000000;
  font-weight: bold;
  font-size: 11px;
}
.pos-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  table-layout: fixed;
}
.pos-table th {
  font-weight: bold;
  font-size: 12px;
  border-bottom: 1px dashed #000;
  white-space: nowrap;
}
th.col-num, th.col-amt {
  text-align: right !important;
}
.pos-table td {
  padding: 1px 0px;
  vertical-align: top;
  word-break: break-word;
}
.col-sl {
  width: 10%;
  font-weight: bold;
  color:#000000;
}
.col-item {
  width: 40%;
  font-weight: bold;
  color:#000000;
}
.col-num {
  text-align: right;
  width: 15%;
  padding:0 2px;
  font-weight: bold;
  color:#000000;
}
.col-amt {
  text-align: right;
  width: 20%;
  padding:0 2px;
  font-weight: bold;
  color:#000000;
}
.summary-section {
  margin: 3px 0;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 1px 0;
  font-size: 11px;
}
.summary-row span{
   font-weight: bold;
   color: #000000;
}
.summary-netbill {
  font-weight: bold;
  font-size: 12px;
  border-top: 1px solid #000;
  border-bottom: 1px solid #000;
  padding: 2px 0;
  margin: 2px 0;
}
.thank-you {
  text-align: center;
  margin-top: 2px;
  font-size: 10px;
  font-weight: bold;
}
.text-center {
  text-align: center;
}
</style>
